import { defineInjectable, isInjectable } from './Injectable.js';
import { isService } from './Service.js';
/**
 * Symbol used to identify container instances.
 * @internal
 */
export const CONTAINER_SYMBOL = Symbol('container');
/**
 * Injectable that provides access to the container itself.
 * Useful for services that need to perform custom dependency resolution.
 */
export const Container = defineInjectable({
    name: 'Container',
    resolve: container => container,
});
/**
 * Creates a new dependency injection container with the specified options.
 * @param options - Optional configuration for the container
 * @returns A new container instance
 * @__NO_SIDE_EFFECTS__
 */
export function createContainer(options) {
    const instances = new Map();
    const injectables = new Map();
    const parent = options?.parent;
    const scope = options?.scope;
    const resolve = options?.resolve ?? ((service, source) => service.resolve(container, source));
    const createChild = options?.createChild ?? (childScope => createContainer({ parent: container, scope: childScope }));
    const container = {
        [CONTAINER_SYMBOL]: true,
        scope,
        parent,
        addModule: (module, ...params) => module(container, ...params),
        createChild,
        get,
        set,
        register,
        resolve: resolve,
        tryResolve,
    };
    return container;
    function get(injectable) {
        return (instances.get(injectable) ?? injectables.get(injectable));
    }
    function set(injectable, instanceOrService) {
        if (isInjectable(instanceOrService)) {
            injectables.set(injectable, instanceOrService);
        }
        else {
            instances.set(injectable, instanceOrService);
        }
    }
    function register(service) {
        if (service.implements) {
            injectables.set(service.implements, service);
        }
    }
    function tryResolve(injectable, source) {
        const instance = instances.get(injectable);
        if (instance) {
            return instance;
        }
        // Try to resolve as a registered service
        const service = injectables.get(injectable);
        if (service) {
            return resolveService(service, source);
        }
        if (isService(injectable)) {
            return resolveService(injectable, source);
        }
        return parent?.tryResolve(injectable, source);
    }
    function resolveService(service, source) {
        if (service.scope !== scope) {
            return parent?.tryResolve(service, source);
        }
        return resolve(service, source);
    }
}
/**
 * Type guard to check if a value is a Container instance.
 * @param value - The value to check
 * @returns True if the value is a Container, false otherwise
 */
export function isContainer(value) {
    return typeof value === 'object' && value != null && CONTAINER_SYMBOL in value;
}
//# sourceMappingURL=Container.js.map